import {
    DocumentArrowDownIcon,
    DocumentPlusIcon,
    EnvelopeIcon,
    LinkIcon,
    PrinterIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import {
    Answer,
    CalendarDate,
    CommunityId,
    ContactId,
    FileId,
    formatDateNoTime,
    IAnswer,
    ICommunity,
    IFile,
    InteractionType,
    IQuestion,
    LayoutSectionParentKey,
    LicenseStatus,
    QuestionCategories,
    questionMetadata,
    QuestionSource,
    User,
} from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { NgByline } from "components/byline/ng-byline";
import { CommunitiesComparePdfDialog } from "components/clients/show-client/community-comparison/community-pdf-dialog";
import { ContractPaymentScheduleEditor } from "components/communities/show-community/contract-payment-schedule-editor";
import { CommunityCardTypes } from "components/community-search/community-card/community-card-types";
import { Activity } from "components/entity-activity/entity-activity";
import { AntiFeature, Feature } from "components/feature";
import { FilesControl } from "components/files";
import { SignatureRequestPanel } from "components/files/signature-request/signature-request-panel";
import { EntityPageHeader } from "components/layout";
import { EntityTab } from "components/layout/entity-tab";
import { EntityConversationFeed } from "components/messages/entity-conversation-feed";
import { MessageCompose } from "components/messages/message-compose";
import { MessagesTabHeader } from "components/messages/messages-tab-header";
import { ImportantNoteBanner } from "components/notes/important-note-banner";
import { CreateReferralLinkPanel } from "components/references/create-referral-link-panel";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { Col, NgGrid, Row } from "components/shared/grid";
import { Subnav } from "components/shared/subnav";
import { SubnavItem } from "components/shared/subnav/subnav-types";
import { EntityTasks, TaskBadge } from "components/tasks/child";
import {
    ActionsMenu,
    ActionsMenuAction,
    ActionsMenuSection,
    SignWiseMenuAction,
} from "components/ui/actions-menu";
import { AutosavingInput } from "components/ui/autosaving-input";
import { CalendarDateInput } from "components/ui/calendar-date";
import { Checkbox } from "components/ui/checkbox";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SelectPro } from "components/ui/select-pro";
import { AdvancedSearchEntityType, FileEntityType } from "generated/graphql";
import React, { useCallback, useState } from "react";
import { ResponsiveMode } from "store/reducers/responsive";
import { useFeature } from "store/selectors/hooks";
import { communityToListableCommunity } from "../../community-search/listable-community-utility";
import { Header } from "../../header";
import { Question } from "../../questions";
import { CommunityAdvancedView } from "../advanced";
import { CommunityLicenses } from "../licenses";
import { BuiltInPriceFields } from "./built-in-price-fields";
import { CommunityActionBar } from "./community-action-bar";
import { CommunityAliasesHeaderHint } from "./community-aliases-widget";
import { CommunityInsightsTab } from "./community-insights-tab";
import { CommunityIntakeUploadPanel } from "./community-intake-upload-panel";
import { CommunityPlacementHistory } from "./community-placement-history";
import { CommunityReferrals } from "./community-referrals";
import { CommunityToursTab } from "./community-tours-tab";
import { InvoiceTab } from "./invoice-tab";

export type Subpage =
    | "details"
    | "attributes"
    | "records"
    | "history"
    | "tours"
    | "tasks"
    | "referrals"
    | "invoices"
    | "files"
    | "emails"
    | "advanced"
    | "placements"
    | "insights";

interface ShowCommunityProps {
    community: ICommunity;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onFieldChange: (fieldName: string, newValue: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onFieldChanges: (changes: Record<string, any>) => void;
    onReloadData: () => void;
    questions: IQuestion[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onAddPhotos?: (communityId: CommunityId, photos: File[]) => Promise<any>;
    onUpdatePhotos?: (
        communityId: CommunityId,
        photos: Partial<IFile>[],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    ) => Promise<any>;
    onUpdatePhotoDescription?: (
        communityId: CommunityId,
        photoId: FileId,
        description: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    ) => Promise<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onDeletePhoto?: (communityId: CommunityId, photoId: FileId) => Promise<any>;
    onUpdateAnswer: (answer: IAnswer) => Promise<IAnswer>;
    selectSubpage: (subpage: string) => void;
    closeSection: () => void;
    activeSubpage: Subpage;
    delete: () => Promise<void>;
    responsiveMode: ResponsiveMode;
    now: Date;
    users: { [id: string]: User };
    fileCount: number;
    contractsEnabled: boolean;
    newlyAddedContactPerson?: ContactId;
    showInvoicesTab: boolean;
    showAdvancedTab: boolean;
    canUpdateCommunitySearch: boolean;
    navigateToPrintIntakeForm: () => void;
}

export const ShowCommunity: React.FC<ShowCommunityProps> = props => {
    const {
        community,
        showInvoicesTab,
        showAdvancedTab,
        onUpdateAnswer,
        onFieldChange,
        onFieldChanges,
        canUpdateCommunitySearch,
        contractsEnabled,
        responsiveMode,
        users,
        fileCount,
        activeSubpage,
        delete: deleteCommunity,
        onReloadData,
        selectSubpage,
        closeSection,
        navigateToPrintIntakeForm,
    } = props;

    const communityPaperIntakeEnabled = useFeature("communityPaperIntake");

    const communityDetailsTab = useCallback(() => {
        return (
            <EntityTab
                columns={[
                    LayoutSectionParentKey.CommunityDetailsColumn1,
                    LayoutSectionParentKey.CommunityDetailsColumn2,
                ]}
                entity={community}
                entityId={community.id}
                entityType="Community"
                answerMode={QuestionSource.Legacy}
                onAnswerCommitted={(_entityId, answer) => onUpdateAnswer(answer)}
                onFieldChange={onFieldChange}
            />
        );
    }, [community, onUpdateAnswer, onFieldChange]);

    const controlForQuestion = useCallback(
        (question: IQuestion) => {
            if (question.interaction == InteractionType.none) {
                return null;
            }

            const answer =
                community.answers.find(x => x.questionId == question.id) || new Answer();

            return (
                <Question
                    key={question.id}
                    callback={onUpdateAnswer}
                    question={question}
                    answer={answer}
                />
            );
        },
        [onUpdateAnswer, community.answers],
    );

    const hasQuestionsToDisplay = useCallback(
        (category: QuestionCategories): boolean => {
            const questions = props.questions || [];
            const questionsInCategory = questions.filter(
                q => q.category == category && q.interaction != InteractionType.none,
            );
            const questionsEnabled = questionsInCategory.filter(q =>
                q.enabled(props.questions, community.answers),
            );
            return questionsEnabled.length > 0;
        },
        [props.questions, community.answers],
    );

    const questionSectionWithExtras = useCallback(
        (
            category: QuestionCategories,
            preJSX?: JSX.Element,
            postJSX?: JSX.Element,
        ): JSX.Element => {
            if (preJSX == null && postJSX == null && !hasQuestionsToDisplay(category))
                return null;
            const questions = props.questions
                .filter(
                    q =>
                        q.category == category &&
                        q.enabled(props.questions, community.answers),
                )
                .map(q => controlForQuestion(q));
            const details = questionMetadata[category];

            return (
                <InputFormBlock>
                    <Header iconName={details.icon}>{details.communityHeading}</Header>
                    <div className="space-y-2 md:space-y-3">
                        {preJSX}
                        {questions}
                        {postJSX}
                    </div>
                </InputFormBlock>
            );
        },
        [hasQuestionsToDisplay, controlForQuestion, props.questions, community.answers],
    );

    const questionSection = useCallback(
        (category: QuestionCategories): JSX.Element => {
            return questionSectionWithExtras(category, null);
        },
        [questionSectionWithExtras],
    );

    const customQuestionCheckboxes = useCallback((): JSX.Element => {
        return (
            <>
                {questionSection(QuestionCategories.type)}
                {questionSection(QuestionCategories.staff)}
                {questionSection(QuestionCategories.services)}
                {questionSection(QuestionCategories.amenity)}
            </>
        );
    }, [questionSection]);

    const priceCommitHelper = useCallback(
        (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
            newValue: any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
            oldValue: any,
            priceProp: string,
            priceModifiedProp: string,
        ) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
            const changes: Record<string, any> = {};
            // We don't want to update the lastModifiedPrice if the user simply
            // clicked in the box and back out.
            if (newValue || null != oldValue || null) {
                changes[priceProp] = newValue;
                changes[priceModifiedProp] = new Date().toISOString();
                onFieldChanges(changes);
            }
        },
        [onFieldChanges],
    );

    const onCommitPricingNotes = useCallback(
        (newValue: unknown) => {
            priceCommitHelper(
                newValue,
                community.priceDetails,
                "priceDetails",
                "pricingNotesLastModified",
            );
        },
        [community, priceCommitHelper],
    );

    const getLastUpdatedLabel = useCallback((prop: unknown): string | null => {
        if (prop) {
            return "(last updated " + formatDateNoTime(prop) + ")";
        }

        return null;
    }, []);

    const preFinanceFields = useCallback((): JSX.Element => {
        return (
            <BuiltInPriceFields
                onFieldChange={onFieldChange}
                getLastUpdatedLabel={getLastUpdatedLabel}
                priceCommitHelper={priceCommitHelper}
                community={community}
            />
        );
    }, [community, onFieldChange, getLastUpdatedLabel, priceCommitHelper]);

    const postFinanceFields = useCallback((): JSX.Element => {
        return (
            <div className="input-form-section custom-question">
                <AutosavingInput
                    label="Other Pricing Notes"
                    initial={community.priceDetails}
                    onCommit={newVal => onCommitPricingNotes(newVal)}
                    multiLine={true}
                />
                <div className="text-sm">
                    {getLastUpdatedLabel(community.pricingNotesLastModified)}
                </div>
            </div>
        );
    }, [
        community.priceDetails,
        community.pricingNotesLastModified,
        getLastUpdatedLabel,
        onCommitPricingNotes,
    ]);

    const standardDetailFields = useCallback((): JSX.Element => {
        return (
            <>
                <div className="gender-dropdown">
                    <SelectPro
                        label="Gender restrictions?"
                        includePlaceholderOption
                        value={community.gender}
                        onChange={e => onFieldChange("gender", e.target.value)}
                        options={[
                            {
                                value: "both",
                                text: "Accepts male and female",
                            },
                            { value: "female", text: "Female only" },
                            { value: "male", text: "Male only" },
                        ]}
                    />
                </div>
                <AutosavingInput
                    label="Number of residents per caregiver"
                    initial={`${community.caregiverRatio}`}
                    onCommit={newVal => onFieldChange("caregiverRatio", newVal)}
                    type="number"
                />
                <AutosavingInput
                    label="Total beds"
                    initial={`${community.unitsOrCapacity}`}
                    onCommit={newVal => onFieldChange("unitsOrCapacity", newVal)}
                    type="number"
                />
            </>
        );
    }, [
        onFieldChange,
        community.gender,
        community.caregiverRatio,
        community.unitsOrCapacity,
    ]);

    const communityAttributesTab = useCallback(() => {
        return (
            <NgGrid>
                <NgGrid.Column>
                    {questionSectionWithExtras(
                        QuestionCategories.finance,
                        preFinanceFields(),
                        postFinanceFields(),
                    )}
                    {questionSection(QuestionCategories.activity)}
                    {questionSectionWithExtras(
                        QuestionCategories.otherDetails,
                        standardDetailFields(),
                    )}
                </NgGrid.Column>
                <NgGrid.Column>{customQuestionCheckboxes()}</NgGrid.Column>
            </NgGrid>
        );
    }, [
        questionSection,
        questionSectionWithExtras,
        customQuestionCheckboxes,
        standardDetailFields,
        postFinanceFields,
        preFinanceFields,
    ]);

    const standardContractFields = useCallback((): JSX.Element => {
        return (
            <>
                {community.id ? (
                    <ContractPaymentScheduleEditor
                        key={community.id}
                        communityId={community.id}
                        paymentType={community.paymentType}
                        paymentPercentage={community.paymentPercentage}
                        paymentFee={community.paymentFee}
                        onFieldChange={onFieldChange}
                    />
                ) : null}
                <SelectPro
                    label={"Contract Status"}
                    includePlaceholderOption
                    placeholderStr="(unspecified)"
                    value={community.contractStatus}
                    onChange={e => onFieldChange("contractStatus", e.target.value)}
                    options={[
                        { value: "pending", text: "Pending" },
                        { value: "active", text: "Active" },
                        { value: "oneOff", text: "One off / Honor Contract" },
                        { value: "none", text: "No contract" },
                        { value: "terminated", text: "Terminated" },
                    ]}
                />

                <div className="contract-issued-date">
                    <CalendarDateInput
                        label="Contract Valid Since"
                        initial={
                            community.contractIssuedDate
                                ? CalendarDate.parse(
                                      community.contractIssuedDate,
                                  ).getOrElse(null)
                                : null
                        }
                        onChange={cd =>
                            onFieldChange(
                                "contractIssuedDate",
                                cd ? cd.toString() : undefined,
                            )
                        }
                    />
                </div>
                <div className="contract-expires-date">
                    <CalendarDateInput
                        label="Contract Expires"
                        initial={
                            community.contractExpiresDate
                                ? CalendarDate.parse(
                                      community.contractExpiresDate,
                                  ).getOrElse(null)
                                : null
                        }
                        onChange={cd =>
                            onFieldChange(
                                "contractExpiresDate",
                                cd ? cd.toString() : undefined,
                            )
                        }
                    />
                </div>
                <AutosavingInput
                    label="Contract Notes / Terms"
                    initial={community.contractNotes}
                    onCommit={newVal => onFieldChange("contractNotes", newVal)}
                    multiLine={true}
                />
                <Checkbox
                    disabled={!canUpdateCommunitySearch}
                    onChange={newVal =>
                        onFieldChange("showInSearchResults", newVal.target.checked)
                    }
                    checked={community.showInSearchResults}
                    label="Allow clients to be matched with this community"
                />
            </>
        );
    }, [
        canUpdateCommunitySearch,
        community.showInSearchResults,
        community.id,
        onFieldChange,
        community.paymentType,
        community.paymentPercentage,
        community.paymentFee,
        community.contractStatus,
        community.contractIssuedDate,
        community.contractExpiresDate,
        community.contractNotes,
    ]);

    const standardRecordFields = useCallback((): JSX.Element => {
        return (
            <div className="input-form-section">
                <Feature name="discreteLicenseRecords">
                    <CommunityLicenses communityId={community.id} />
                </Feature>
                <AntiFeature name="discreteLicenseRecords">
                    <SelectPro
                        label="State License Status"
                        includePlaceholderOption
                        placeholderStr="(unspecified)"
                        value={community.licenseStatus}
                        onChange={e => onFieldChange("licenseStatus", e.target.value)}
                        options={[
                            { value: "active", text: LicenseStatus.active },
                            { value: "closed", text: LicenseStatus.closed },
                            {
                                value: "notApplicable",
                                text: LicenseStatus.notApplicable,
                            },
                            {
                                value: "onProbation",
                                text: LicenseStatus.onProbation,
                            },
                            { value: "pending", text: LicenseStatus.pending },
                        ]}
                    />
                    <div className="licensed-since-date">
                        <CalendarDateInput
                            label="Community/Facility Licensed Date:"
                            initial={
                                community.licensedSinceDate
                                    ? CalendarDate.parse(
                                          community.licensedSinceDate,
                                      ).getOrElse(null)
                                    : null
                            }
                            onChange={cd =>
                                onFieldChange(
                                    "licensedSinceDate",
                                    cd ? cd.toString() : undefined,
                                )
                            }
                        />
                    </div>
                    <Feature name="discreteLicenseeField">
                        <div className="">
                            <AutosavingInput
                                label="Licensee"
                                initial={community.licensee}
                                onCommit={newVal => onFieldChange("licensee", newVal)}
                            />
                        </div>
                    </Feature>
                </AntiFeature>
            </div>
        );
    }, [
        community.id,
        community.licenseStatus,
        community.licensedSinceDate,
        community.licensee,
        onFieldChange,
    ]);

    const contractTab = useCallback((): JSX.Element => {
        const recordsColumn = (
            <div className="input-form-section">
                {questionSectionWithExtras(
                    QuestionCategories.communityRecords,
                    standardRecordFields(),
                )}
            </div>
        );

        if (!contractsEnabled) {
            return (
                <Row type="flex" className="collapsible">
                    <Col className="full-width">{recordsColumn}</Col>
                </Row>
            );
        }

        const contractColumn = questionSectionWithExtras(
            QuestionCategories.contract,
            standardContractFields(),
        );

        return (
            <NgGrid>
                <NgGrid.Column>{contractColumn}</NgGrid.Column>
                <NgGrid.Column>{recordsColumn}</NgGrid.Column>
            </NgGrid>
        );
    }, [
        contractsEnabled,
        questionSectionWithExtras,
        standardContractFields,
        standardRecordFields,
    ]);

    const placementHistoryTab = useCallback((): JSX.Element => {
        return (
            <CommunityPlacementHistory
                communityId={community.id}
                responsiveMode={responsiveMode}
                users={users}
            />
        );
    }, [community.id, responsiveMode, users]);

    const referralsTab = useCallback((): JSX.Element => {
        return (
            <section className="list-clients">
                <CommunityReferrals community={community} />
            </section>
        );
    }, [community]);

    const invoicesTab = useCallback((): JSX.Element => {
        return (
            <section className="list-clients">
                <InvoiceTab community={community} />
            </section>
        );
    }, [community]);

    const emailsSentTab = useCallback((): JSX.Element => {
        return (
            <EntityConversationFeed
                entityId={community.id}
                entityType={AdvancedSearchEntityType.Community}
            />
        );
    }, [community]);

    const tasksTab = useCallback((): JSX.Element => {
        return (
            <Row type="flex" className="collapsible">
                <Col className="full-width">
                    <EntityTasks
                        parentId={community.id}
                        entityInfo={{
                            displayName: community.name || "(no name)",
                            path: `/communities/show/${community.id}`,
                            kind: "Community",
                        }}
                    />
                </Col>
            </Row>
        );
    }, [community]);

    const filesTab = useCallback((): JSX.Element => {
        return (
            <FilesControl entityId={community.id} entityType={FileEntityType.Community} />
        );
    }, [community]);

    const advancedTab = useCallback((): JSX.Element => {
        return (
            <CommunityAdvancedView
                community={{
                    id: community.id,
                    name: community.name || "",
                    address: community.address,
                    city: community.city,
                    state: community.state,
                }}
            />
        );
    }, [community]);

    const activityTab = useCallback((): JSX.Element => {
        return <Activity entityId={community.id} entityType="Community" />;
    }, [community]);

    const evergreenTab = useCallback((): JSX.Element => {
        return <CommunityInsightsTab communityId={community.id} />;
    }, [community]);

    const toursTab = useCallback((): JSX.Element => {
        return (
            <CommunityToursTab
                communityId={community.id}
                communityName={community.name || ""}
                users={users}
            />
        );
    }, [community.id, community.name, users]);

    const subpages: SubnavItem[] = [
        {
            href: `/communities/show/${community.id}/details`,
            subpage: "details",
            linkText: "Details",
            render: communityDetailsTab,
        },
        {
            href: `/communities/show/${community.id}/attributes`,
            subpage: "attributes",
            linkText: "Attributes",
            render: communityAttributesTab,
        },
    ];

    subpages.push({
        href: `/communities/show/${community.id}/evergreen`,
        subpage: "insights",
        linkText: "Insights",
        render: evergreenTab,
    });

    subpages.push({
        href: `/communities/show/${community.id}/records`,
        subpage: "records",
        linkText: contractsEnabled ? "Records & Contracts" : "Records",
        render: contractTab,
    });

    subpages.push({
        href: `/communities/show/${community.id}/tours`,
        subpage: "tours",
        linkText: "Tours",
        render: toursTab,
    });

    subpages.push({
        href: `/communities/show/${community.id}/placements`,
        subpage: "placements",
        linkText: "Placements",
        render: placementHistoryTab,
    });
    subpages.push({
        href: `/communities/show/${community.id}/referrals`,
        subpage: "referrals",
        linkText: "Referrals",
        render: referralsTab,
    });

    if (showInvoicesTab) {
        subpages.push({
            href: `/communities/show/${community.id}/invoices`,
            subpage: "invoices",
            linkText: "Invoices",
            render: invoicesTab,
        });
    }
    subpages.push({
        href: `/communities/show/${community.id}/emails`,
        subpage: "emails",
        linkText: <MessagesTabHeader />,
        render: emailsSentTab,
    });
    subpages.push({
        href: `/communities/show/${community.id}/tasks`,
        subpage: "tasks",
        linkText: "Tasks",
        render: tasksTab,
        badge: <TaskBadge parentId={community.id} />,
    });
    subpages.push({
        href: `/communities/show/${community.id}/files`,
        subpage: "files",
        linkText: `Files`,
        badge: { count: fileCount, type: "info" as const },
        render: filesTab,
    });
    subpages.push({
        href: `/communities/show/${community.id}/history`,
        subpage: "history",
        linkText: "History",
        render: activityTab,
    });

    if (showAdvancedTab) {
        subpages.push({
            href: `/communities/show/${community.id}/advanced`,
            subpage: "advanced",
            linkText: "Advanced",
            render: advancedTab,
        });
    }

    const [inPdfWorkflow, setInPdfWorkflow] = useState(false);
    const [isComposing, setIsComposing] = useState(false);
    const [showSignaturePanel, setShowSignaturePanel] = useState(false);
    const [showCreateReferralLinkPanel, setShowCreateReferralLinkPanel] = useState(false);
    const [showCommunityIntakeUploadPanel, setShowCommunityIntakeUploadPanel] =
        useState(false);

    const communityName = useCallback((): string => {
        return community.name || "(no name)";
    }, [community.name]);

    const shareReferralLinkEnabled = useFeature("shareReferralLink");

    const startPdf = useCallback(() => {
        setInPdfWorkflow(true);
    }, []);

    const compose = useCallback(() => {
        setIsComposing(true);
    }, []);

    const closeCompose = useCallback(() => {
        setIsComposing(false);
    }, []);

    const openSignaturePanel = useCallback((): void => {
        setShowSignaturePanel(true);
    }, []);

    const closeSignaturePanel = useCallback((): void => {
        setShowSignaturePanel(false);
    }, []);

    const openCreateReferralLinkPanel = useCallback((): void => {
        setShowCreateReferralLinkPanel(true);
    }, []);

    const closeCreateReferralLinkPanel = useCallback((): void => {
        setShowCreateReferralLinkPanel(false);
    }, []);

    const openCommunityIntakeUploadPanel = useCallback((): void => {
        setShowCommunityIntakeUploadPanel(true);
    }, []);

    const closeCommunityIntakeUploadPanel = useCallback((): void => {
        setShowCommunityIntakeUploadPanel(false);
    }, []);

    const startDelete = useCallback(async (): Promise<void> => {
        const result = await fancyConfirm(
            `Delete ${communityName()}?`,
            `Really delete ${communityName()}?`,
            "Yes, delete",
            "Cancel",
            { extraConfirm: true },
        );
        if (result) {
            deleteCommunity();
        }
    }, [communityName, deleteCommunity]);

    const headerBar = useCallback(() => {
        return (
            <EntityPageHeader>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 md:gap-x-4 gap-y-1 min-w-0">
                        <h2 className="text-base md:text-xl lg:text-2xl truncate min-w-[150px] flex-shrink">
                            {communityName()}
                        </h2>
                        <div className="flex-shrink truncate">
                            <CommunityCardTypes
                                community={communityToListableCommunity(community)}
                            />
                        </div>
                    </div>
                    <CommunityAliasesHeaderHint
                        community={community}
                        onRename={newName => onFieldChange("name", newName)}
                    />
                </div>
                <div className="flex items-center space-x-2 md:space-x-4 flex-shrink-0 self-start">
                    <div>
                        <NgByline entity={community} mode="header" />
                    </div>
                    <div>
                        <ActionsMenu>
                            <ActionsMenuSection>
                                <ActionsMenuAction
                                    Icon={EnvelopeIcon}
                                    menuHidden={false}
                                    onClick={compose}>
                                    Send email
                                </ActionsMenuAction>
                                <SignWiseMenuAction
                                    onClick={openSignaturePanel}></SignWiseMenuAction>
                                <ActionsMenuAction
                                    Icon={DocumentArrowDownIcon}
                                    menuHidden={false}
                                    onClick={startPdf}>
                                    Save community PDF
                                </ActionsMenuAction>
                                <ActionsMenuAction
                                    Icon={DocumentPlusIcon}
                                    menuHidden={!communityPaperIntakeEnabled}
                                    onClick={openCommunityIntakeUploadPanel}>
                                    Upload Paper Intake Form
                                </ActionsMenuAction>
                                <ActionsMenuAction
                                    Icon={LinkIcon}
                                    menuHidden={!shareReferralLinkEnabled}
                                    onClick={openCreateReferralLinkPanel}>
                                    Share referral link
                                </ActionsMenuAction>
                            </ActionsMenuSection>
                            <ActionsMenuAction
                                Icon={PrinterIcon}
                                menuHidden={false}
                                onClick={navigateToPrintIntakeForm}>
                                Printable Intake Form
                            </ActionsMenuAction>
                            <ActionsMenuSection>
                                <ActionsMenuAction
                                    Icon={TrashIcon}
                                    menuHidden={false}
                                    onClick={startDelete}>
                                    Delete
                                </ActionsMenuAction>
                            </ActionsMenuSection>
                        </ActionsMenu>
                    </div>
                </div>
            </EntityPageHeader>
        );
    }, [
        communityName,
        compose,
        openSignaturePanel,
        startPdf,
        openCommunityIntakeUploadPanel,
        openCreateReferralLinkPanel,
        startDelete,
        community,
        onFieldChange,
        shareReferralLinkEnabled,
        communityPaperIntakeEnabled,
        navigateToPrintIntakeForm,
    ]);

    const composeWindow = useCallback((): JSX.Element | null => {
        if (!isComposing) {
            return null;
        }
        return (
            <MessageCompose
                entityType={AdvancedSearchEntityType.Community}
                entityId={community.id}
                onDismiss={closeCompose}
            />
        );
    }, [isComposing, community.id, closeCompose]);

    const dialogs = useCallback((): JSX.Element => {
        return (
            <div>
                {composeWindow()}
                {inPdfWorkflow && (
                    <CommunitiesComparePdfDialog
                        action={`/api/communities/${community.id}/pdf`}
                        onDismiss={() => setInPdfWorkflow(false)}
                    />
                )}
                <SignatureRequestPanel
                    fileEntityType={FileEntityType.Community}
                    entityId={community.id}
                    isOpen={showSignaturePanel}
                    onDismiss={closeSignaturePanel}
                />
                {showCreateReferralLinkPanel ? (
                    <CreateReferralLinkPanel
                        referenceCommunityId={community.id}
                        onDismiss={closeCreateReferralLinkPanel}
                    />
                ) : null}
                {showCommunityIntakeUploadPanel ? (
                    <CommunityIntakeUploadPanel
                        communityId={community.id}
                        onDismiss={closeCommunityIntakeUploadPanel}
                    />
                ) : null}
            </div>
        );
    }, [
        composeWindow,
        inPdfWorkflow,
        showSignaturePanel,
        showCreateReferralLinkPanel,
        showCommunityIntakeUploadPanel,
        closeCommunityIntakeUploadPanel,
        closeSignaturePanel,
        closeCreateReferralLinkPanel,
        community.id,
    ]);

    const actionBar = useCallback((): JSX.Element => {
        return (
            <CommunityActionBar
                community={community}
                onFieldChange={onFieldChange}
                onReloadData={onReloadData}
            />
        );
    }, [community, onFieldChange, onReloadData]);

    return (
        <div>
            <div className="row">
                <div className="component-wrapper col">
                    {headerBar()}
                    {dialogs()}
                    {actionBar()}
                    <ImportantNoteBanner entityId={community.id} />
                    <DetailPageLayout>
                        <Subnav
                            navigate={selectSubpage}
                            selectedSubpage={activeSubpage}
                            subpages={subpages}
                            closeSection={closeSection}
                            defaultSubpage="details"
                        />
                    </DetailPageLayout>
                </div>
            </div>
        </div>
    );
};
